import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { useTheme } from '../../context/ThemeContext';
import { usePermissions } from '../../context/PermissionsContext';
import { PERMISSIONS } from '../../constants/permissions';
import { ROLE_COPY, TENANCY_COPY } from '../../constants/partnerCopy';
import { clearAuthData } from '../../utils/apiKeyAuth';
import {
  LayoutDashboard,
  Cpu,
  Wrench,
  Library,
  Info,
  Brain,
  Layers,
  ChevronLeft,
  ChevronRight,
  Menu as MenuIcon,
  X as CloseIcon,
  Palette,
  Sun,
  Moon,
  CircleUserRound,
  Shield,
  LogOut,
  Webhook,
  // Calendar,
  Zap,
  RefreshCw,
  KeyRound,
  ShieldCheck,
  UsersRound,
  FolderHeart,
  Building2,
  History,
  Plug,
  Link2,
  Network,
  Handshake,
} from 'lucide-react';
import styles from './Sidebar.module.scss';

const SIDEBAR_EXPANDED_PX = 256;
const SIDEBAR_COLLAPSED_PX = 80;

// The admin keys that unlock the "Company Admin" console (spec §4). Note that
// resource_sharing is deliberately NOT here: it only grants the Company Resources
// browse/share screen, never Users/Roles/LLM-keys administration.
const COMPANY_ADMIN_KEYS = [
  PERMISSIONS.USER_MANAGEMENT,
  PERMISSIONS.ROLE_MANAGEMENT,
  PERMISSIONS.LLM_KEYS_COMPANY,
  PERMISSIONS.USAGE_ANALYTICS,
];

// `permissions` = show when the user has ANY of the listed keys.
// `requirePlatformAdmin` = show only for platform admins.
// `requireLoaded` = one key that must be in the loaded permission list (fail closed).
// No gate field = always visible. (See spec §3.2 for the key → screen mapping.)
const menuItems = [
  { id: 'home', label: 'Home', icon: LayoutDashboard },
  { id: 'agents', label: 'Agents', icon: Cpu, permissions: [PERMISSIONS.AGENTS] },
  { id: 'templates', label: 'Templates', icon: Layers, permissions: [PERMISSIONS.TEMPLATES] },
  // { id: 'knowledge', label: 'Rules book', icon: BookOpen },
  // { id: 'knowledgeD3', label: 'Customer Data', icon: CloudUpload },
  { id: 'knowledgeHub', label: 'Knowledge Base', icon: Library, permissions: [PERMISSIONS.KB_DIM1, PERMISSIONS.KB_DIM3] },
  { id: 'knowledgeGraph', label: 'Knowledge Graph', icon: Network, permissions: [PERMISSIONS.KNOWLEDGE_GRAPH] },
  { id: 'models', label: 'Models', icon: Brain },
  { id: 'tools', label: 'Tools', icon: Wrench, permissions: [PERMISSIONS.COMPOSIO, PERMISSIONS.CUSTOM_TOOLS] },
  { id: 'mcpServers', label: 'MCP Servers', icon: Plug, permissions: [PERMISSIONS.MCP_SERVERS] },
  { id: 'webhooks', label: 'Webhooks', icon: Webhook, permissions: [PERMISSIONS.WEBHOOKS] },
  { id: 'triggers', label: 'Triggers', icon: Zap, permissions: [PERMISSIONS.TRIGGERS] },
  { id: 'batchJobs', label: 'Batch Jobs', icon: RefreshCw, permissions: [PERMISSIONS.BATCH_JOBS] },
  { id: 'runHistory', label: 'Run History', icon: History, permissions: [PERMISSIONS.DASHBOARD] },
  { id: 'myLlmKeys', label: 'My LLM Keys', icon: KeyRound, permissions: [PERMISSIONS.LLM_KEYS_USER] },
  // Off by default for everyone, so it fails CLOSED (hidden until permissions load).
  { id: 'platformMcp', label: 'Connect Platform MCP', icon: Link2, requireLoaded: PERMISSIONS.PLATFORM_MCP },
  { id: 'sharedWithMe', label: 'Shared with me', icon: FolderHeart },
  // Sharing-delegates (resource_sharing without any admin key) get the browse
  // screen as a plain collaboration item — NOT under an "admin" label (spec §4).
  { id: 'companyResources', label: 'Company Resources', icon: Building2, requireCompanyResources: true },
  { id: 'companyAdmin', label: 'Company Admin', icon: UsersRound, requireCompanyAdmin: true },
  { id: 'partnerAdmin', label: 'Partner Admin', icon: Handshake, requirePartner: true },
  { id: 'companies', label: 'Platform Admin', icon: ShieldCheck, requirePlatformAdmin: true },
  // Schedules — hidden from sidebar; route remains at /schedules
  // { id: 'schedules', label: 'Schedules', icon: Calendar },
 // { id: 'contexts', label: 'Global Contexts', icon: FileText },
 // { id: 'manager', label: '4ay Manager', icon: CircleUser },
];

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userFooterWrapRef = useRef(null);
  const userMenuPanelRef = useRef(null);
  const { user, loading } = useUser();
  const { isDarkMode, toggleTheme } = useTheme();
  const {
    has,
    hasAny,
    isPlatformAdmin,
    company,
    partner,
    isPartnerAdmin,
    isPartnerStaff,
    partnerPermissions,
    permissions,
    ready,
  } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const showLabels = mobileMenuOpen || !isCollapsed;

  // Hide nav items the user's permissions/entitlements don't grant (spec §3.2).
  // Regular feature items fail OPEN while permissions load (hasAny returns true),
  // so a bootstrap hiccup never blanks the menu. Privileged items (Platform/Company
  // Admin) fail CLOSED — hidden until we've confirmed the user qualifies — so they
  // never flash to non-admins on refresh.
  const visibleMenuItems = menuItems.filter((item) => {
    if (item.requirePlatformAdmin) return isPlatformAdmin;
    // Exactly what /user/me/permissions lists — no platform-admin bypass, so
    // platform staff without the key never see it (platform admins get it listed).
    if (item.requireLoaded) {
      return ready && (permissions.includes(item.requireLoaded) || permissions.includes('*'));
    }
    // Partner Admin: any partner_* key (Partner tier guide §3.2). The list is
    // empty until permissions load, so this fails closed.
    if (item.requirePartner) return partnerPermissions.length > 0;
    // Company Admin: shown ONLY to company members holding at least one admin key
    // (user_management / role_management / llm_keys_company — spec §4).
    // resource_sharing alone must never surface an "admin" menu. `company` is null
    // until permissions load, so this stays hidden (fail-closed) during the
    // bootstrap window too.
    if (item.requireCompanyAdmin) {
      // Partner staff administer everything else from the Partner area; the only
      // company-admin screen left for them is custom roles for direct users.
      if (isPartnerStaff) return has(PERMISSIONS.ROLE_MANAGEMENT);
      return !!company && hasAny(COMPANY_ADMIN_KEYS);
    }
    // Company Resources: the resource_sharing browse/share screen, surfaced next
    // to "Shared with me" for sharing-delegates who hold no admin key. Admins see
    // the same screen as a tab inside Company Admin instead, so we never show both.
    if (item.requireCompanyResources) {
      const showsAdminItem = isPartnerStaff
        ? has(PERMISSIONS.ROLE_MANAGEMENT)
        : hasAny(COMPANY_ADMIN_KEYS);
      return !!company && !showsAdminItem && has(PERMISSIONS.RESOURCE_SHARING);
    }
    if (item.permissions && item.permissions.length) return hasAny(item.permissions);
    return true;
  });

  const syncSidebarWidth = useCallback(() => {
    const mq = window.matchMedia('(min-width: 769px)');
    if (!mq.matches) {
      document.documentElement.style.removeProperty('--app-sidebar-width');
      return;
    }
    const w = isCollapsed ? SIDEBAR_COLLAPSED_PX : SIDEBAR_EXPANDED_PX;
    document.documentElement.style.setProperty('--app-sidebar-width', `${w}px`);
  }, [isCollapsed]);

  useEffect(() => {
    syncSidebarWidth();
    const mq = window.matchMedia('(min-width: 769px)');
    mq.addEventListener('change', syncSidebarWidth);
    return () => {
      mq.removeEventListener('change', syncSidebarWidth);
    };
  }, [syncSidebarWidth]);

  const positionUserMenu = useCallback(() => {
    const anchor = userFooterWrapRef.current;
    const menu = userMenuPanelRef.current;
    if (!anchor || !menu) return;
    const rect = anchor.getBoundingClientRect();
    const gap = 8;
    const w = window.innerWidth;
    const h = window.innerHeight;

    menu.style.position = 'fixed';
    menu.style.zIndex = '10050';
    menu.style.right = 'auto';

    if (showLabels) {
      menu.style.left = `${rect.left}px`;
      menu.style.width = `${rect.width}px`;
      menu.style.minWidth = '';
      menu.style.bottom = `${h - rect.top + gap}px`;
      menu.style.top = 'auto';
      const spaceAbove = rect.top - gap;
      if (spaceAbove < 280) {
        menu.style.maxHeight = `${Math.max(120, spaceAbove - 8)}px`;
        menu.style.overflowY = 'auto';
      } else {
        menu.style.maxHeight = '';
        menu.style.overflowY = '';
      }
    } else {
      menu.style.maxHeight = `${Math.min(420, h - 16)}px`;
      menu.style.overflowY = 'auto';
      menu.style.bottom = 'auto';
      const panelW = 280;
      let left = rect.right + gap;
      if (left + panelW > w - 8) {
        left = Math.max(8, rect.left - panelW - gap);
      }
      menu.style.left = `${left}px`;
      menu.style.width = `${panelW}px`;
      menu.style.minWidth = `${panelW}px`;
      menu.style.top = `${Math.max(8, Math.min(rect.top, h - 8 - 320))}px`;
      menu.style.bottom = 'auto';
      requestAnimationFrame(() => {
        const mb = menu.getBoundingClientRect();
        if (mb.bottom > h - 8) {
          menu.style.top = `${Math.max(8, h - mb.height - 8)}px`;
        }
      });
    }
  }, [showLabels]);

  useLayoutEffect(() => {
    if (!showUserMenu) return;
    positionUserMenu();
    const onReposition = () => positionUserMenu();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [showUserMenu, showLabels, isCollapsed, mobileMenuOpen, positionUserMenu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showUserMenu) return;
      const panel = userMenuPanelRef.current;
      const trigger = userFooterWrapRef.current;
      if (
        panel &&
        trigger &&
        !panel.contains(event.target) &&
        !trigger.contains(event.target)
      ) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      const t = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      return () => {
        clearTimeout(t);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showUserMenu]);

  const handleMenuClick = (itemId) => {
    setMobileMenuOpen(false);

    switch (itemId) {
      case 'home':
        navigate('/');
        break;
      case 'agents':
        navigate('/agents');
        break;
      case 'templates':
        navigate('/templates');
        break;
      case 'tools':
        navigate('/configure/tools');
        break;
      case 'mcpServers':
        navigate('/mcp-servers');
        break;
      case 'knowledgeHub':
        navigate('/knowledge-hub');
        break;
      case 'knowledgeGraph':
        navigate('/knowledge-graph');
        break;
      case 'models':
        navigate('/models');
        break;
      case 'webhooks':
        navigate('/configure/webhooks');
        break;
      case 'triggers':
        navigate('/configure/triggers');
        break;
      case 'schedules':
        navigate('/schedules');
        break;
      case 'batchJobs':
        navigate('/batch-jobs');
        break;
      case 'runHistory':
        navigate('/run-history');
        break;
      case 'myLlmKeys':
        navigate('/my-llm-keys');
        break;
      case 'platformMcp':
        navigate('/platform-mcp');
        break;
      case 'sharedWithMe':
        navigate('/shared-with-me');
        break;
      case 'companyResources':
        // Same route as the console — with resource_sharing only, the screen
        // renders just the Company Resources view (no admin tabs).
        navigate('/company-admin');
        break;
      case 'companyAdmin':
        navigate('/company-admin');
        break;
      case 'partnerAdmin':
        navigate('/partner-admin');
        break;
      case 'companies':
        navigate('/companies');
        break;
      default:
        console.log(`Navigation for ${itemId} not implemented yet`);
        break;
    }
  };

  const isMenuItemActive = (itemId) => {
    switch (itemId) {
      case 'home':
        return location.pathname === '/';
      case 'agents':
        return location.pathname === '/agents';
      case 'templates':
        return location.pathname === '/templates';
      case 'tools':
        return location.pathname === '/configure/tools';
      case 'mcpServers':
        return location.pathname.startsWith('/mcp-servers');
      case 'knowledgeHub':
        return (
          location.pathname === '/knowledge-hub' ||
          location.pathname.startsWith('/knowledge-base')
        );
      case 'knowledgeGraph':
        return location.pathname.startsWith('/knowledge-graph');
      case 'models':
        return location.pathname === '/models';
      case 'webhooks':
        return location.pathname === '/configure/webhooks';
      case 'schedules':
        return location.pathname.startsWith('/schedules');
      case 'batchJobs':
        return location.pathname.startsWith('/batch-jobs');
      case 'runHistory':
        return location.pathname.startsWith('/run-history');
      case 'myLlmKeys':
        return location.pathname === '/my-llm-keys';
      case 'platformMcp':
        return location.pathname === '/platform-mcp';
      case 'sharedWithMe':
        return location.pathname === '/shared-with-me';
      case 'companyResources':
        return location.pathname.startsWith('/company-admin');
      case 'companyAdmin':
        return location.pathname.startsWith('/company-admin');
      case 'partnerAdmin':
        return location.pathname.startsWith('/partner-admin');
      case 'companies':
        return location.pathname.startsWith('/companies');
      default:
        return false;
    }
  };

  const displayName = loading
    ? 'Loading…'
    : user?.name || user?.email?.split('@')[0] || 'User';
  // Tenancy chip (Partner tier guide §3.2). Partner staff see the partner, never
  // the hidden home company; client-company users see their company plus a
  // subtle "Managed by <partner>" line. Everyone else keeps the existing label.
  let displayRole = user?.plan || 'Community Plan';
  let orgNote = null;
  let orgName = user?.organization || `${displayName}'s organization`;
  if (isPartnerStaff && partner?.name) {
    displayRole = isPartnerAdmin
      ? TENANCY_COPY.partnerAdminChip(partner.name)
      : TENANCY_COPY.partnerStaffChip(partner.name);
    orgName = partner.name;
  } else if (company?.name && partner?.name) {
    displayRole = company.name;
    orgNote = TENANCY_COPY.managedBy(partner.name);
    orgName = company.name;
  } else if (company?.name) {
    orgName = company.name;
  }

  const itemLabel = (item) =>
    item.id === 'companyAdmin' && isPartnerStaff ? ROLE_COPY.directUsersRoles : item.label;

  return (
    <>
      <button
        type="button"
        className={styles.mobileMenuButton}
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? <CloseIcon size={24} /> : <MenuIcon size={24} />}
      </button>

      {mobileMenuOpen && (
        <div
          className={styles.mobileOverlay}
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden
        />
      )}

      <aside
        className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : ''} ${
          mobileMenuOpen ? styles.mobileOpen : ''
        }`}
      >
        <div className={styles.inner}>
          <div
            className={`${styles.topRow} ${!showLabels ? styles.topRowCollapsed : ''}`}
          >
            {showLabels && (
              <div className={styles.brand}>
                <div className={styles.brandMark}>
                  <Cpu size={20} strokeWidth={2.5} className={styles.brandMarkIcon} />
                </div>
                <span className={styles.brandTitle}>Agent Studio</span>
              </div>
            )}
            {!showLabels && (
              <div className={styles.brandMark}>
                <Cpu size={20} strokeWidth={2.5} className={styles.brandMarkIcon} />
              </div>
            )}
            <button
              type="button"
              className={styles.collapseBtn}
              onClick={() => setIsCollapsed(!isCollapsed)}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          </div>

          <nav className={`${styles.nav} scrollable-no-scrollbar`}>
            {visibleMenuItems.map((item) => {
              const Icon = item.icon;
              const active = isMenuItemActive(item.id);
              const label = itemLabel(item);
              return (
                <div key={item.id} className={styles.navItemWrap}>
                  <button
                    type="button"
                    title={!showLabels ? label : undefined}
                    className={`${styles.navBtn} ${active ? styles.navBtnActive : ''} ${
                      item.hasHighlight ? styles.navBtnHighlight : ''
                    } ${!showLabels ? styles.navBtnIconOnly : ''}`}
                    onClick={() => handleMenuClick(item.id)}
                  >
                    <Icon
                      size={20}
                      strokeWidth={active ? 2.5 : 2}
                      className={styles.navBtnIcon}
                    />
                    {showLabels && <span className={styles.navLabel}>{label}</span>}
                    {showLabels && item.hasHighlight && (
                      <span className={styles.highlightDot} aria-hidden />
                    )}
                  </button>

                  {item.hasHighlight && showLabels && (
                    <div className={styles.ruleTooltip}>
                      <div className={styles.ruleTooltipInner}>
                        <Info size={14} className={styles.ruleTooltipInfo} />
                        <p className={styles.ruleTooltipText}>
                          <span className={styles.ruleTooltipTitle}>
                            Configure the rule book
                          </span>
                          This defines how your systems behave. The rulebook represents the
                          logic layer used by autonomous systems.
                        </p>
                      </div>
                      <div className={styles.ruleTooltipArrow} />
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {showLabels && (
            <div className={styles.creditsCard}>
              <div className={styles.creditsCardHeader}>
                <span className={styles.creditsCardLabel}>Credits Left</span>
                <span className={styles.creditsCardValue}>499.57</span>
              </div>
              <div className={styles.creditsBar}>
                <div className={styles.creditsBarFill} style={{ width: '75%' }} />
              </div>
              <p className={styles.creditsCardHint}>Refills in 12 days</p>
            </div>
          )}

          <div className={styles.footerDivider} />

          <div ref={userFooterWrapRef} className={styles.userFooterWrap}>
            <button
              type="button"
              className={`${styles.userFooter} ${!showLabels ? styles.userFooterCollapsed : ''}`}
              onClick={() => setShowUserMenu((v) => !v)}
            >
              <div className={styles.userAvatar}>
                {loading ? (
                  <span className={styles.userAvatarLetter}>…</span>
                ) : (
                  <span className={styles.userAvatarLetter}>
                    {(user?.name?.charAt(0) || user?.email?.charAt(0) || 'U').toUpperCase()}
                  </span>
                )}
              </div>
              {showLabels && (
                <div className={styles.userMeta}>
                  <span className={styles.userName}>{displayName}</span>
                  <span className={styles.userRole} title={displayRole}>{displayRole}</span>
                  {orgNote && (
                    <span className={styles.userOrgNote} title={orgNote}>{orgNote}</span>
                  )}
                </div>
              )}
            </button>
          </div>

          {showUserMenu &&
            createPortal(
              <div
                ref={userMenuPanelRef}
                className={styles.userMenuPanel}
                onClick={(e) => e.stopPropagation()}
                role="menu"
              >
                <div className={styles.accountMenuHeader}>
                  <span className={styles.accountMenuHeaderText}>Organizations</span>
                </div>
                <div className={styles.accountMenuRow}>
                  <span className={styles.orgBadge}>
                    {(orgName || 'U').charAt(0).toUpperCase()}
                  </span>
                  <span className={styles.orgName}>{orgName}</span>
                  <span className={styles.orgCheck}>✓</span>
                </div>
                <div className={styles.accountMenuDivider} />
                <div
                  className={styles.accountMenuRow}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Palette size={18} className={styles.accountMenuIcon} />
                  <span className={styles.accountMenuLabel}>Theme</span>
                  <div
                    className={styles.themeToggle}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTheme();
                    }}
                  >
                    {isDarkMode ? <Moon size={18} /> : <Sun size={18} />}
                    <div className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        className={styles.toggleInput}
                        checked={isDarkMode}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleTheme();
                        }}
                      />
                      <span className={styles.toggleSlider} />
                    </div>
                  </div>
                </div>
                <div className={styles.accountMenuRow}>
                  <CircleUserRound size={18} className={styles.accountMenuIcon} />
                  <span className={styles.accountMenuLabel}>Account &amp; API Keys</span>
                </div>
                <div className={styles.accountMenuRow}>
                  <Shield size={18} className={styles.accountMenuIcon} />
                  <span className={styles.accountMenuLabel}>Privacy Policy</span>
                </div>
                <div className={styles.accountMenuDivider} />
                <div
                  className={styles.accountMenuRow}
                  onClick={() => {
                    clearAuthData();
                    window.location.href = '/login';
                  }}
                >
                  <LogOut size={18} className={styles.accountMenuIcon} />
                  <span className={`${styles.accountMenuLabel} ${styles.logoutLabel}`}>
                    Log out
                  </span>
                </div>
              </div>,
              document.body
            )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
